import React, { useState, useEffect } from 'react';
import ApiKeyLogin from './ApiKeyLogin';
import { MediaOutputsPanel } from './MediaOutputsPanel';
import { OmniAdminPanel } from './OmniAdminPanel';
import MCPToolsPanel from './MCPToolsPanel';
import AgentModePanel from './AgentModePanel';
import VideoAnalysis from './VideoAnalysis';
import MobileAceStep from './MobileAceStep';
import { DraggableDashboard } from './DraggableDashboard';
import { LiveLogsPanel } from './LiveLogsPanel';
import MobileOmnigateUse from './MobileOmnigateUse';
import { TelemetryDashboard } from './TelemetryDashboard';

import { KernelSettings, SystemStats, SystemLog, QueueItem } from '../types';

// ─── Props ────────────────────────────────────────────────────────────────
interface TelegramMobileAppProps {
  webApp: any;
  isAuthenticated: boolean;
  onAuthenticated: (key: string) => void;
  kernel: KernelSettings;
  setKernel: React.Dispatch<React.SetStateAction<KernelSettings>>;
  stats: SystemStats;
  setStats: React.Dispatch<React.SetStateAction<SystemStats>>;
  addLog: (level: SystemLog['level'], message: string, component?: string) => void;
  queue: QueueItem[];
  setQueue: React.Dispatch<React.SetStateAction<QueueItem[]>>;
  logs: SystemLog[];
  clearLogs: () => void;
  handleAgentTool: (call: any) => void;
  setIsMainChatActive: React.Dispatch<React.SetStateAction<boolean>>;
  isAudit1200Mode: boolean;
  isChatFocusMode: boolean;
}

type MobileTab = 'hub' | 'chat' | 'agents' | 'acestep' | 'omnigateuse' | 'media' | 'admin' | 'tools' | 'stream' | 'logs' | 'telemetry';

// ─── Component ────────────────────────────────────────────────────────────
const TelegramMobileApp: React.FC<TelegramMobileAppProps> = (props) => {
  const {
    webApp, isAuthenticated, onAuthenticated, kernel, setKernel,
    stats, setStats, addLog, queue, setQueue, logs, clearLogs,
    handleAgentTool, setIsMainChatActive, isAudit1200Mode, isChatFocusMode
  } = props;

  const [activeTab, setActiveTab] = useState<MobileTab>('hub');
  const [headerTitle, setHeaderTitle] = useState('OmniGate');

  // ─── Admin Quick State ──────────────────────────────────────────────────
  const [aceStatus, setAceStatus] = useState<string>('?');
  const [backendStatus, setBackendStatus] = useState<string>('?');
  const apiKey = localStorage.getItem('omnigate_api_key') || '';
  const authHeaders = { 'X-API-Key': apiKey, 'Authorization': `Bearer ${apiKey}` };

  // ─── Haptics ────────────────────────────────────────────────────────────
  const triggerHaptic = (style: 'light' | 'medium' | 'heavy' = 'light') => {
    if (webApp?.HapticFeedback) webApp.HapticFeedback.impactOccurred(style);
  };

  const handleTabChange = (tab: MobileTab, title: string) => {
    setActiveTab(tab);
    setHeaderTitle(title);
    triggerHaptic('light');
  };

  // ─── Admin Checks ──────────────────────────────────────────────────────
  const refreshAdminStatus = async () => {
    try {
      const r1 = await fetch('/v1/acestep/status', { headers: authHeaders });
      const d1 = await r1.json();
      setAceStatus(d1.api_running ? '🟢 Online' : '🔴 Offline');
    } catch { setAceStatus('🔴 Error'); }
    try {
      const r2 = await fetch('/admin/system-status', { headers: authHeaders });
      if (r2.ok) setBackendStatus('🟢 Online');
      else setBackendStatus('🟡 Degraded');
    } catch { setBackendStatus('🔴 Unreachable'); }
  };

  useEffect(() => {
    if (isAuthenticated) refreshAdminStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    const handleAgentRun = (e: any) => {
      handleTabChange('omnigateuse', 'OmniUse');
    };
    window.addEventListener('omnigate-agent-run', handleAgentRun);
    return () => window.removeEventListener('omnigate-agent-run', handleAgentRun);
  }, []);

  // ─── Auth Gate ──────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="w-full h-[100dvh] flex items-center justify-center px-6 bg-[#0a0c10] text-[#d1d4dc]">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black tracking-tighter uppercase italic">
              <span className="opacity-80">Omni</span><span className="text-purple-500">Gate</span>
            </h1>
            <p className="text-[10px] opacity-30 uppercase tracking-[0.3em] mt-1">neural infrastructure</p>
          </div>
          <ApiKeyLogin onAuthenticated={onAuthenticated} />
        </div>
      </div>
    );
  }

  // ─── Hub Modules ────────────────────────────────────────────────────────
  const hubModules = [
    { id: 'chat',    icon: 'fa-table-columns',   color: 'text-purple-400', label: 'Dashboard',    desc: 'Main UI + Chat'     },
    { id: 'acestep', icon: 'fa-music',            color: 'text-blue-400',   label: 'ACE-Step',     desc: 'Audio generator'    },
    { id: 'omnigateuse', icon: 'fa-brain',          color: 'text-purple-400', label: 'OmniUse',   desc: 'AI Agent tasks'     },
    { id: 'admin',   icon: 'fa-server',           color: 'text-red-400',    label: 'Admin Root',   desc: 'Sysadmin panel'     },
    { id: 'stream',  icon: 'fa-video',            color: 'text-green-400',  label: 'Live Stream',  desc: 'Vision monitoring'  },
    { id: 'agents',  icon: 'fa-robot',            color: 'text-yellow-400', label: 'Agents',       desc: 'Worker management'  },
    { id: 'media',   icon: 'fa-photo-film',       color: 'text-pink-400',   label: 'Gallery',      desc: 'Image outputs'      },
    { id: 'tools',   icon: 'fa-wrench',           color: 'text-orange-400', label: 'MCP Tools',    desc: 'Integrations'       },
    { id: 'logs',    icon: 'fa-rectangle-list',   color: 'text-cyan-400',   label: 'Live Logs',    desc: 'System tracing'     },
    { id: 'telemetry', icon: 'fa-chart-pie',      color: 'text-teal-400',   label: 'Telemetry',    desc: 'API Usage & Limits' },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-[100dvh] w-full overflow-hidden bg-[#0a0c10] text-[#d1d4dc]">

      {/* ─── HEADER ─────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/10 shadow-sm z-20 bg-[#131722]">
        {activeTab !== 'hub' && (
          <button onClick={() => handleTabChange('hub', 'OmniGate')} className="p-1 -ml-1 opacity-70 active:opacity-100">
            <i className="fa-solid fa-chevron-left text-lg"></i>
          </button>
        )}
        <h1 className="text-lg font-black tracking-tighter uppercase italic text-center flex-1">
          {headerTitle === 'OmniGate'
            ? <><span className="opacity-80">Omni</span><span className="text-purple-500">Gate</span></>
            : headerTitle}
        </h1>
        {activeTab !== 'hub' && <div className="w-6"></div>}
      </div>

      {/* ─── MAIN DYNAMIC AREA ──────────────────────────────────── */}
      <div className="flex-1 overflow-hidden relative">

        {/* HUB */}
        {activeTab === 'hub' && (
          <div className="h-full overflow-y-auto p-4 pb-24 space-y-4 no-scrollbar">
            {/* Quick status cards */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <span className="text-[9px] uppercase font-black opacity-40 block">Backend</span>
                <span className="text-xs font-bold">{backendStatus}</span>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <span className="text-[9px] uppercase font-black opacity-40 block">ACE-Step</span>
                <span className="text-xs font-bold">{aceStatus}</span>
              </div>
            </div>

            {/* Module grid */}
            <div className="grid grid-cols-2 gap-2.5">
              {hubModules.map(mod => (
                <button key={mod.id}
                  onClick={() => handleTabChange(mod.id as MobileTab, mod.label)}
                  className="flex flex-col items-start p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] active:scale-[0.96] active:bg-white/[0.08] transition-all text-left">
                  <i className={`fa-solid ${mod.icon} ${mod.color} text-xl mb-2.5`}></i>
                  <span className="font-bold text-[13px] text-white mb-0.5">{mod.label}</span>
                  <span className="text-[10px] opacity-35 leading-tight">{mod.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── DASHBOARD (Chat) ─────────────────────────────────── */}
        {activeTab === 'chat' && (
          <div className="h-full w-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '1100px', minHeight: '900px', zoom: 0.7, padding: '8px' }}>
              <DraggableDashboard
                stats={stats} setStats={setStats} addLog={addLog} queue={queue}
                setQueue={setQueue} logs={logs} clearLogs={clearLogs}
                handleAgentTool={handleAgentTool} setIsMainChatActive={setIsMainChatActive}
                isAudit1200Mode={isAudit1200Mode} isChatFocusMode={isChatFocusMode}
              />
            </div>
          </div>
        )}

        {/* ─── ACE-STEP ─────────────────────────────────────────── */}
        {activeTab === 'acestep' && (
          <div className="h-full overflow-y-auto p-4 pb-24 no-scrollbar">
            <MobileAceStep triggerHaptic={triggerHaptic} />
          </div>
        )}

        {/* ─── OMNIGATE USE ─────────────────────────────────────── */}
        {activeTab === 'omnigateuse' && (
          <div className="h-full overflow-y-auto p-4 pb-24 no-scrollbar">
            <MobileOmnigateUse triggerHaptic={triggerHaptic} />
          </div>
        )}

        {/* ─── ADMIN ROOT ───────────────────────────────────────── */}
        {activeTab === 'admin' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '1000px', zoom: 0.8, padding: '8px' }}>
              <OmniAdminPanel />
            </div>
          </div>
        )}

        {/* ─── LIVE STREAM ──────────────────────────────────────── */}
        {activeTab === 'stream' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '900px', zoom: 0.8, padding: '8px' }}>
              <VideoAnalysis settings={kernel} setKernel={setKernel} isMainChatActive={false} />
            </div>
          </div>
        )}

        {/* ─── AGENTS ───────────────────────────────────────────── */}
        {activeTab === 'agents' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '1200px', zoom: 0.7, padding: '8px' }}>
              <AgentModePanel />
            </div>
          </div>
        )}

        {/* ─── MEDIA GALLERY ────────────────────────────────────── */}
        {activeTab === 'media' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '800px', zoom: 0.85, padding: '8px' }}>
              <MediaOutputsPanel />
            </div>
          </div>
        )}

        {/* ─── MCP TOOLS ────────────────────────────────────────── */}
        {activeTab === 'tools' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '1000px', zoom: 0.8, padding: '8px' }}>
              <MCPToolsPanel />
            </div>
          </div>
        )}

        {/* ─── LIVE LOGS ────────────────────────────────────────── */}
        {activeTab === 'logs' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '800px', zoom: 0.8, padding: '8px' }}>
              <LiveLogsPanel />
            </div>
          </div>
        )}

        {/* ─── TELEMETRY ────────────────────────────────────────── */}
        {activeTab === 'telemetry' && (
          <div className="h-full overflow-auto touch-pan-x touch-pan-y bg-[#0a0c10]">
            <div style={{ minWidth: '1000px', zoom: 0.8, padding: '8px' }}>
              <TelemetryDashboard />
            </div>
          </div>
        )}
      </div>

      {/* ─── BOTTOM NAV BAR ─────────────────────────────────────── */}
      <div className="flex-shrink-0 flex justify-around items-center border-t border-white/10 pt-1.5 pb-[max(6px,env(safe-area-inset-bottom))] z-50 backdrop-blur-xl bg-[#1e222d]/95">
        {[
          { id: 'hub',     icon: 'fa-home',           label: 'Hub'    },
          { id: 'chat',    icon: 'fa-table-columns',   label: 'Main'   },
          { id: 'acestep', icon: 'fa-music',           label: 'Audio'  },
    { id: 'omnigateuse', icon: 'fa-brain',          color: 'text-purple-400', label: 'OmniUse',   desc: 'AI Agent tasks'     },
          { id: 'stream',  icon: 'fa-video',           label: 'Stream' },
          { id: 'admin',   icon: 'fa-shield-halved',   label: 'Admin'  },
        ].map(item => {
          const isActive = activeTab === item.id;
          return (
            <button key={item.id}
              onClick={() => handleTabChange(item.id as MobileTab, item.id === 'hub' ? 'OmniGate' : item.label)}
              className={`flex flex-col items-center justify-center w-full py-1 transition-all ${isActive ? 'text-blue-400' : 'opacity-40'}`}>
              <i className={`fa-solid ${item.icon} ${isActive ? 'text-lg' : 'text-base'} mb-0.5 transition-all`}></i>
              <span className="text-[8px] font-black uppercase tracking-wider">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TelegramMobileApp;
